import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authService } from '../services/auth';
import { organizationService } from '../services/organization';
import { roleConfigService } from '../services/roleConfig';
import { savePageAccessConfig, saveAppPages } from '../utils/roleAccess';
import toast from 'react-hot-toast';

async function fetchAndCacheRoleConfig(): Promise<void> {
  try {
    const [config, pages] = await Promise.all([
      roleConfigService.getConfig(),
      roleConfigService.getPages(),
    ]);
    savePageAccessConfig(config);
    saveAppPages(pages);
  } catch {
    // keep existing cache
  }
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/** Resolves organizationCode + organizationName for both admin and employee users. */
async function resolveOrgInfo(userProfile: User): Promise<void> {
  try {
    if (userProfile.organizationCode) {
      const org = await organizationService.getOrganizationByCode(userProfile.organizationCode);
      userProfile.organizationName = org.name;
    } else if (userProfile.organizationId) {
      const org = await organizationService.getOrganizationById(userProfile.organizationId);
      userProfile.organizationCode = org.code;
      userProfile.organizationName = org.name;
    }
  } catch (error) {
    console.error('Failed to fetch organization info:', error);
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUser = authService.getStoredUser();
        if (storedUser && authService.isAuthenticated()) {
          // Fetch fresh user data and organization info
          const freshUser = await authService.getProfile();
          
          await resolveOrgInfo(freshUser);
          await fetchAndCacheRoleConfig();
          setUser(freshUser);
          localStorage.setItem('user', JSON.stringify(freshUser));
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        authService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      setIsLoading(true);
      const authResponse = await authService.login({ username, password });
      
      // Store tokens first so they can be used in subsequent requests
      authService.storeAuthData(authResponse);
      
      // After login, get user profile
      const userProfile = await authService.getProfile();
      
      await resolveOrgInfo(userProfile);
      await fetchAndCacheRoleConfig();

      // Store user profile
      localStorage.setItem('user', JSON.stringify(userProfile));
      setUser(userProfile);
      toast.success(`Welcome back, ${userProfile.name || userProfile.firstName}!`);
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: any) => {
    try {
      setIsLoading(true);
      const userProfile = await authService.register(data);
      
      // After registration, login to get tokens
      const authResponse = await authService.login({ username: data.email, password: data.password });
      
      // Store tokens first
      authService.storeAuthData(authResponse);
      
      await resolveOrgInfo(userProfile);
      await fetchAndCacheRoleConfig();

      // Store user profile
      localStorage.setItem('user', JSON.stringify(userProfile));
      setUser(userProfile);
      toast.success(`Welcome to Restro360, ${userProfile.name || userProfile.firstName}!`);
    } catch (error: any) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    toast.success('Logged out successfully');
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const value = {
    user,
    isLoading,
    login,
    register,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};